import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjects, Project, generateLandingPage, LandingPageGenerate, getMasterplanStreams, getMasterplanArticles, MasterplanArticle, getBrandGuidesByProject, BrandGuide, generateValueProposition, getQuizConfigs, getLandingPageTemplates, LandingPageTemplate, LandingPageTemplateCategory } from '../services/api';
import AIFieldGenerator from './AIFieldGenerator';
import { GlassCard } from './ui/GlassCard';
import { GlassButton } from './ui/GlassButton';
import { GlassInput } from './ui/GlassInput';
import { GlassSelect } from './ui/GlassSelect';
import { ModuleHeader } from './ui/ModuleHeader';
import {
  UserPlus, ArrowRightCircle, Mail, FileText, Clock, Sparkles,
  ShoppingBag, Calendar, Video, Heart, MailMinus, Columns, Layers,
  Lock,
} from 'lucide-react';
import './LandingPageCreator.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface LandingPageCreatorProps {
  onCancel?: () => void;
  onSuccess?: () => void;
  projectId?: string;
}

// Map template icon names to Lucide components
const TEMPLATE_ICONS: Record<string, React.ReactNode> = {
  'user-plus': <UserPlus size={24} />,
  'arrow-right-circle': <ArrowRightCircle size={24} />,
  'mail': <Mail size={24} />,
  'file-text': <FileText size={24} />,
  'clock': <Clock size={24} />,
  'sparkles': <Sparkles size={24} />,
  'shopping-bag': <ShoppingBag size={24} />,
  'calendar': <Calendar size={24} />,
  'video': <Video size={24} />,
  'heart': <Heart size={24} />,
  'mail-minus': <MailMinus size={24} />,
  'columns': <Columns size={24} />,
  'layers': <Layers size={24} />,
};

const LandingPageCreator: React.FC<LandingPageCreatorProps> = ({
  onCancel,
  onSuccess,
  projectId
}) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');

  // Template state
  const [templates, setTemplates] = useState<LandingPageTemplate[]>([]);
  const [templateCategories, setTemplateCategories] = useState<LandingPageTemplateCategory[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<LandingPageTemplate | null>(null);

  const [masterplanStreams, setMasterplanStreams] = useState<string[]>([]);
  const [articles, setArticles] = useState<MasterplanArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<MasterplanArticle | null>(null);
  const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
  const [selectedBrandGuide, setSelectedBrandGuide] = useState<BrandGuide | null>(null);
  const [isGeneratingBenefits, setIsGeneratingBenefits] = useState(false);
  const [benefitsMessage, setBenefitsMessage] = useState('');
  const [existingQuizzes, setExistingQuizzes] = useState<any[]>([]);

  // Helper to normalize stream names (remove leading numbers, newlines, parenthetical suffixes, extra whitespace)
  const normalizeStream = (s: string): string => {
    return s
      .replace(/^\d+[\.\)]\s*/, '')           // Remove leading numbers like "1)" or "1."
      .replace(/\s*\(.*?\)\s*/g, '')          // Remove parenthetical suffixes like "(Melbourne/VIC)"
      .replace(/\s+/g, ' ')                   // Normalize whitespace
      .trim();
  };

  const [formData, setFormData] = useState<LandingPageGenerate>({
    project_id: projectId || '',
    stream: '',
    title: '',
    slug: '',
    campaign_goal: 'consultation',
    value_proposition: '',
    target_segment_override: '',
    style: 'professional',
    include_quiz: true,
    quiz_purpose: '',
    existing_quiz_id: '',
    include_lead_form: true,
    include_testimonials: false,
    testimonials_content: '',
    include_faq: true,
    faq_questions: '',
    services_content: '',
    primary_color: '#2563eb',
    source_website_url: '',
    llm_model: 'gemini'
  });

  useEffect(() => {
    loadProjects();
    loadTemplates();
    if (projectId) {
      setFormData(prev => ({ ...prev, project_id: projectId }));
      loadStreams(projectId);
      loadQuizzes(projectId);
    }
  }, [projectId]);

  useEffect(() => {
    if (formData.project_id) {
      loadStreams(formData.project_id);
      loadBrandGuides(formData.project_id);
      loadQuizzes(formData.project_id);
    }
  }, [formData.project_id]);

  // Load articles when stream changes
  useEffect(() => {
    if (formData.project_id && formData.stream) {
      loadArticles(formData.project_id, formData.stream);
    } else {
      setArticles([]);
      setSelectedArticle(null);
    }
  }, [formData.project_id, formData.stream]);

  const loadProjects = async () => {
    try {
      const data = await getProjects();
      setProjects(data);
      // Auto-select "Unassigned" if user has no projects and no project pre-selected
      if (!projectId && data.length === 0) {
        setFormData(prev => ({ ...prev, project_id: 'unassigned' }));
      }
    } catch (err) {
      setError('Failed to load projects');
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await getLandingPageTemplates();
      setTemplates(data.templates);
      setTemplateCategories(data.categories);
    } catch (err) {
      console.log('Failed to load templates');
    }
  };

  const loadQuizzes = async (pid: string) => {
    try {
      const data = await getQuizConfigs(pid);
      setExistingQuizzes(data);
    } catch (err) {
      console.log('Failed to load quizzes');
    }
  };

  const loadStreams = async (projectId: string) => {
    try {
      const response = await getMasterplanStreams(projectId);
      // Normalize stream names: remove leading numbers like "1)" or "1." and newlines
      const normalizedStreams = (response.streams || []).map((s: string) => normalizeStream(s));
      setMasterplanStreams(normalizedStreams);
    } catch (err) {
      console.log('No masterplan streams available for this project');
      setMasterplanStreams([]);
    }
  };

  const loadArticles = async (projectId: string, stream: string) => {
    if (!stream) {
      setArticles([]);
      setSelectedArticle(null);
      return;
    }
    try {
      // Normalize stream name for API call
      const normalizedStream = normalizeStream(stream);
      const articlesData = await getMasterplanArticles(projectId, normalizedStream);
      setArticles(articlesData);
    } catch (err) {
      console.log('No articles available for this stream');
      setArticles([]);
    }
  };

  const loadBrandGuides = async (projectId: string) => {
    try {
      const guides = await getBrandGuidesByProject(projectId);
      setBrandGuides(guides);

      // Auto-select default brand guide if available
      const defaultGuide = guides.find(g => g.is_default);
      if (defaultGuide) {
        setSelectedBrandGuide(defaultGuide);
        handleInputChange('brand_guide_id', defaultGuide.brand_guide_id);
        // Always apply brand guide's primary color when auto-loading
        if (defaultGuide.primary_color) {
          handleInputChange('primary_color', defaultGuide.primary_color);
        }
        if (defaultGuide.design_style) {
          const styleMap: Record<string, string> = {
            'modern': 'modern', 'minimal': 'minimal',
            'professional': 'professional', 'creative': 'creative'
          };
          const mappedStyle = styleMap[defaultGuide.design_style.toLowerCase()];
          if (mappedStyle) handleInputChange('style', mappedStyle);
        }
      }
    } catch (err) {
      console.log('No brand guides available for this project');
      setBrandGuides([]);
    }
  };

  const handleInputChange = (field: keyof LandingPageGenerate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-generate slug from title
    if (field === 'title') {
      const slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }

    // Clear selected article when stream changes
    if (field === 'stream') {
      setSelectedArticle(null);
      // Clear value_proposition since it will be generated from the article
      setFormData(prev => ({ ...prev, value_proposition: '' }));
    }
  };

  const handleArticleSelect = (article: MasterplanArticle) => {
    setSelectedArticle(article);
    // Use the article's summary as the initial value proposition
    // (user can then click "Generate Top 5 Benefits" for AI-enhanced version)
    setFormData(prev => ({ ...prev, value_proposition: article.summary }));
  };

  const handleGenerateBenefits = async () => {
    if (!selectedArticle || !formData.project_id) return;

    setIsGeneratingBenefits(true);
    setBenefitsMessage('');
    try {
      const result = await generateValueProposition({
        project_id: formData.project_id,
        context: {
          article_content: selectedArticle.prompt,
          stream: formData.stream
        }
      });

      const content = result.content || '';

      // Extract only the Top 5 Benefits section onwards, stripping SUMMARY, Headline, Subheadline etc.
      const benefitsMatch = content.match(/\*\*Top \d+ Benefits[:\s]*\*\*\s*([\s\S]*)/i);
      let generated: string;
      if (benefitsMatch) {
        // Got benefits section — strip any trailing sections (Key Insight, Call to Action)
        generated = benefitsMatch[1]
          .replace(/\*\*Key Insight[:\s]*\*\*[\s\S]*/i, '')
          .replace(/\*\*Call to Action[^*]*\*\*[\s\S]*/i, '')
          .trim();
      } else {
        // Fallback: strip common AI preamble
        generated = content.replace(
          /^(?:Sure[!.,]?\s*|Certainly[!.,]?\s*)?(?:Here(?:'s| are| is)\s[\s\S]*?(?:benefits|proposition)[^:\n]*[:\n]\s*)/i,
          ''
        ).trim();
      }

      // Append to existing value proposition instead of replacing
      setFormData(prev => {
        const existing = (prev.value_proposition || '').trim();
        const combined = existing ? `${existing}\n\n${generated}` : generated;
        return { ...prev, value_proposition: combined };
      });
      setBenefitsMessage('Top 5 benefits have been added to the Value Proposition field above.');
      setTimeout(() => setBenefitsMessage(''), 8000);
    } catch (err: any) {
      setError(getApiError(err, 'Failed to generate benefits'));
    } finally {
      setIsGeneratingBenefits(false);
    }
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError('');

    try {
      // Include the selected article's prompt if an article is selected
      const generateData = {
        ...formData,
        // Pass the article prompt if an article was selected
        ...(selectedArticle && { article_prompt: selectedArticle.prompt }),
        // Pass template type if one was selected
        ...(selectedTemplate && selectedTemplate.slug !== 'scratch' && { template_type: selectedTemplate.slug }),
      };

      const result = await generateLandingPage(generateData);
      if (onSuccess) {
        onSuccess();
      } else {
        // Redirect to landing pages list after successful generation
        navigate('/landing-pages');
      }
    } catch (err: any) {
      setError(getApiError(err, 'Failed to generate landing page'));
    } finally {
      setIsGenerating(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        // Template selection step — must pick a template
        return !!selectedTemplate;
      case 2:
        // Require project (or "unassigned"), title, and slug
        const baseRequirements = formData.project_id && formData.title && formData.slug;
        // Only enforce stream selection if there are streams AND a real project is selected
        const streamRequirement = (masterplanStreams.length > 0 && formData.project_id !== 'unassigned')
          ? formData.stream : true;
        return baseRequirements && streamRequirement;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const handleTemplateSelect = (tpl: LandingPageTemplate) => {
    setSelectedTemplate(tpl);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <GlassCard variant="default" padding="lg" className="step-content-card">
            <div className="step-content">
              <h2>Choose a Template</h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '24px', fontSize: '0.9rem' }}>
                Select a landing page type to get started. Each template comes with an optimised layout and AI-tuned copy tone.
              </p>
              {templateCategories.map(cat => {
                const catTemplates = templates.filter(t => t.category === cat.key);
                if (catTemplates.length === 0) return null;
                return (
                  <div key={cat.key} style={{ marginBottom: '28px' }}>
                    <h3 style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>{cat.label}</h3>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '12px', marginTop: 0 }}>{cat.description}</p>
                    <div className="template-grid">
                      {catTemplates.map(tpl => (
                        <div
                          key={tpl.slug}
                          className={`template-card ${selectedTemplate?.slug === tpl.slug ? 'selected' : ''}`}
                          onClick={() => handleTemplateSelect(tpl)}
                        >
                          <div className="template-card-icon">
                            {TEMPLATE_ICONS[tpl.icon] || <Layers size={24} />}
                          </div>
                          <div className="template-card-info">
                            <div className="template-card-name">
                              {tpl.name}
                              {tpl.locked_layout && <Lock size={12} style={{ marginLeft: '6px', opacity: 0.5 }} />}
                            </div>
                            <div className="template-card-desc">{tpl.description}</div>
                          </div>
                          <div className="template-card-sections">
                            {tpl.component_types.length} sections
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        );

      case 2:
        return (
          <GlassCard variant="default" padding="lg" className="step-content-card">
            <div className="step-content">
              <h2>Campaign Details</h2>

              <div className="form-group">
                <GlassSelect
                  label="Project"
                  value={formData.project_id}
                  onChange={(value) => handleInputChange('project_id', value)}
                  options={[
                    { value: '', label: 'Choose a project...' },
                    { value: 'unassigned', label: 'Unassigned (no project)' },
                    ...projects.map(p => ({ value: p.project_id, label: p.name }))
                  ]}
                />
              </div>

              {masterplanStreams.length > 0 && (
                <div className="form-group">
                  <GlassSelect
                    label="Content Stream *"
                    value={formData.stream || ''}
                    onChange={(value) => handleInputChange('stream', value)}
                    options={[
                      { value: '', label: 'Choose a stream...' },
                      ...masterplanStreams.map((stream, idx) => ({ value: stream, label: stream }))
                    ]}
                  />
                  <small>Focus this landing page on a specific content stream/topic</small>
                </div>
              )}

              {/* Article Selector - shows when a stream is selected */}
              {formData.stream && articles.length > 0 && (
                <div className="form-group">
                  <GlassSelect
                    label="Article Reference (Optional)"
                    value={selectedArticle?.title || ''}
                    onChange={(value) => {
                      const article = articles.find(a => a.title === value);
                      if (article) {
                        handleArticleSelect(article);
                      }
                    }}
                    options={[
                      { value: '', label: 'Generate from stream context...' },
                      ...articles.map((article, idx) => ({ value: article.title, label: article.title }))
                    ]}
                  />
                  <small>Select an article to use its content as the value proposition for this landing page</small>
                  {selectedArticle && (
                    <div className="article-preview" style={{
                      marginTop: '0.5rem',
                      padding: '0.75rem',
                      backgroundColor: 'var(--panel-strong)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.875rem',
                      border: '1px solid var(--stroke)'
                    }}>
                      <strong>Selected Article:</strong> {selectedArticle.title}
                      <br />
                      <small style={{ color: 'var(--text-secondary)' }}>
                        {selectedArticle.summary}
                      </small>
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <GlassInput
                  label="Landing Page Title *"
                  value={formData.title}
                  onChange={(value) => handleInputChange('title', value)}
                  placeholder="e.g., Asset Protection Assessment"
                />
              </div>

              <div className="form-group">
                <GlassSelect
                  label="Campaign Objective"
                  value={formData.campaign_goal || 'consultation'}
                  onChange={(value) => handleInputChange('campaign_goal', value)}
                  options={[
                    { value: 'consultation', label: 'Book Consultation' },
                    { value: 'download', label: 'Download Resource' },
                    { value: 'assessment', label: 'Take Assessment' },
                    { value: 'quote', label: 'Request Quote' },
                    { value: 'custom', label: 'Custom' }
                  ]}
                />
                <small>What action do you want visitors to take?</small>
              </div>

              <div className="form-group">
                <label>Value Proposition</label>
                <textarea
                  className="glass-textarea"
                  value={formData.value_proposition || ''}
                  onChange={(e) => handleInputChange('value_proposition', e.target.value)}
                  placeholder="What makes this offer compelling? What value do visitors get?"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-base)',
                    fontFamily: 'var(--font-family)',
                    color: 'var(--text)',
                    background: 'var(--panel)',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    resize: 'vertical',
                    outline: 'none'
                  }}
                />
                <AIFieldGenerator
                  fieldType="value_proposition"
                  projectId={formData.project_id}
                  currentValue={formData.value_proposition}
                  context={{
                    campaign_goal: formData.campaign_goal,
                    stream: formData.stream,
                    title: formData.title,
                    article_content: selectedArticle?.prompt
                  }}
                  onAccept={(content) => handleInputChange('value_proposition', content)}
                  label="Value Proposition"
                  placeholder="Generate a compelling value proposition based on your campaign goal and target audience"
                />
                {selectedArticle && (
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    onClick={handleGenerateBenefits}
                    disabled={isGeneratingBenefits}
                    style={{ marginTop: '8px' }}
                  >
                    {isGeneratingBenefits ? 'Generating...' : '✨ Generate Top 5 Benefits'}
                  </GlassButton>
                )}
                {benefitsMessage && (
                  <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(30, 172, 166, 0.15)', border: '1px solid var(--secondary)', borderRadius: 'var(--radius-md)', color: 'var(--secondary)', fontSize: '13px' }}>
                    {benefitsMessage}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Target Audience</label>
                <textarea
                  className="glass-textarea"
                  value={formData.target_segment_override || ''}
                  onChange={(e) => handleInputChange('target_segment_override', e.target.value)}
                  placeholder="Who is this campaign specifically targeting?"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-base)',
                    fontFamily: 'var(--font-family)',
                    color: 'var(--text)',
                    background: 'var(--panel)',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    backdropFilter: 'blur(20px)',
                    WebkitBackdropFilter: 'blur(20px)',
                    resize: 'vertical',
                    outline: 'none'
                  }}
                />
                <AIFieldGenerator
                  fieldType="target_segment"
                  projectId={formData.project_id}
                  currentValue={formData.target_segment_override}
                  context={{
                    stream: formData.stream,
                    campaign_goal: formData.campaign_goal
                  }}
                  onAccept={(content) => handleInputChange('target_segment_override', content)}
                  label="Target Audience"
                  placeholder="Describe or refine the specific audience segment for this campaign"
                />
                <small>Refine the target audience from your project masterplan if needed</small>
              </div>

              <div className="form-group">
                <GlassInput
                  label="URL Slug *"
                  value={formData.slug}
                  onChange={(value) => handleInputChange('slug', value)}
                  placeholder="asset-protection-assessment"
                />
                <small>URL: https://aime.clearbyte.au/pages/{formData.slug}/</small>
              </div>
            </div>
          </GlassCard>
        );

      case 3:
        return (
          <GlassCard variant="default" padding="lg" className="step-content-card">
            <div className="step-content">
              <h2>Design & Style</h2>

              {brandGuides.length > 0 && (
                <div className="form-group">
                  <GlassSelect
                    label="Brand Guide (Optional)"
                    value={formData.brand_guide_id || ''}
                    onChange={(value) => {
                      const guideId = value;
                      handleInputChange('brand_guide_id', guideId || undefined);

                      // Update colors and style when brand guide is selected
                      if (guideId) {
                        const guide = brandGuides.find(g => g.brand_guide_id === guideId);
                        if (guide) {
                          setSelectedBrandGuide(guide);
                          if (guide.primary_color) {
                            handleInputChange('primary_color', guide.primary_color);
                          }
                          if (guide.design_style) {
                            const styleMap: Record<string, string> = {
                              'modern': 'modern',
                              'minimal': 'minimal',
                              'professional': 'professional',
                              'creative': 'creative'
                            };
                            const mappedStyle = styleMap[guide.design_style.toLowerCase()];
                            if (mappedStyle) {
                              handleInputChange('style', mappedStyle);
                            }
                          }
                        }
                      } else {
                        setSelectedBrandGuide(null);
                      }
                    }}
                    options={[
                      { value: '', label: 'No brand guide (use custom colors)' },
                      ...brandGuides.map(guide => ({
                        value: guide.brand_guide_id,
                        label: `${guide.brand_guide_name} ${guide.is_default ? '(Default)' : ''}`
                      }))
                    ]}
                  />
                  {selectedBrandGuide && (
                    <div style={{
                      padding: 'var(--space-3)',
                      background: 'var(--panel-strong)',
                      borderRadius: 'var(--radius-md)',
                      marginTop: 'var(--space-2)',
                      border: '1px solid var(--stroke)'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px' }}>
                        {selectedBrandGuide.brand_name && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Brand</div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>{selectedBrandGuide.brand_name}</div>
                          </div>
                        )}
                        {selectedBrandGuide.brand_voice && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Voice</div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>{selectedBrandGuide.brand_voice}</div>
                          </div>
                        )}
                        {selectedBrandGuide.primary_color && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Primary Colour</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ display: 'inline-block', width: '16px', height: '16px', borderRadius: '4px', background: selectedBrandGuide.primary_color, border: '1px solid var(--stroke)', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.85rem', color: 'var(--text)', fontFamily: 'monospace' }}>{selectedBrandGuide.primary_color}</span>
                            </div>
                          </div>
                        )}
                        {selectedBrandGuide.secondary_color && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Secondary Colour</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ display: 'inline-block', width: '16px', height: '16px', borderRadius: '4px', background: selectedBrandGuide.secondary_color, border: '1px solid var(--stroke)', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.85rem', color: 'var(--text)', fontFamily: 'monospace' }}>{selectedBrandGuide.secondary_color}</span>
                            </div>
                          </div>
                        )}
                        {selectedBrandGuide.accent_color && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Accent Colour</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ display: 'inline-block', width: '16px', height: '16px', borderRadius: '4px', background: selectedBrandGuide.accent_color, border: '1px solid var(--stroke)', flexShrink: 0 }} />
                              <span style={{ fontSize: '0.85rem', color: 'var(--text)', fontFamily: 'monospace' }}>{selectedBrandGuide.accent_color}</span>
                            </div>
                          </div>
                        )}
                        {selectedBrandGuide.primary_font && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Primary Font</div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>{selectedBrandGuide.primary_font}</div>
                          </div>
                        )}
                        {selectedBrandGuide.secondary_font && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Secondary Font</div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>{selectedBrandGuide.secondary_font}</div>
                          </div>
                        )}
                        {selectedBrandGuide.design_style && (
                          <div>
                            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2px' }}>Design Style</div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text)', textTransform: 'capitalize' }}>{selectedBrandGuide.design_style}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <small>Select a brand guide to automatically apply brand colours and fonts</small>
                </div>
              )}

              <div className="form-group">
                <GlassInput
                  label="Source Website URL (Optional)"
                  type="url"
                  value={formData.source_website_url || ''}
                  onChange={(value) => handleInputChange('source_website_url', value || undefined)}
                  placeholder="https://client-website.com.au"
                />
                <small>We'll analyze this site and match its styling for your landing page</small>
              </div>

              <div className="form-group">
                <label>Style Theme</label>
                <div className="radio-group">
                  {['professional', 'modern', 'minimal', 'creative'].map(style => (
                    <label key={style} className="radio-option">
                      <input
                        type="radio"
                        name="style"
                        value={style}
                        checked={formData.style === style}
                        onChange={(e) => handleInputChange('style', e.target.value)}
                      />
                      <span className="capitalize">{style}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Primary Brand Color</label>
                <div className="color-picker">
                  <input
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => handleInputChange('primary_color', e.target.value)}
                    placeholder="#2563eb"
                    style={{
                      padding: 'var(--space-2) var(--space-3)',
                      fontSize: 'var(--text-base)',
                      color: 'var(--text)',
                      background: 'var(--panel)',
                      border: '1px solid var(--stroke)',
                      borderRadius: 'var(--radius-md)',
                      backdropFilter: 'blur(20px)'
                    }}
                  />
                </div>
              </div>

              <div className="form-group">
                <GlassSelect
                  label="LLM Model"
                  value={formData.llm_model}
                  onChange={(value) => handleInputChange('llm_model', value)}
                  options={[
                    { value: 'gemini', label: 'Gemini (Recommended)' },
                    { value: 'claude', label: 'Claude' },
                    { value: 'grok', label: 'Grok' }
                  ]}
                />
              </div>
            </div>
          </GlassCard>
        );

      case 4:
        return (
          <GlassCard variant="default" padding="lg" className="step-content-card">
            <div className="step-content">
              <h2>Features & Sections</h2>

              <div className="checkbox-group">
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.include_quiz}
                    onChange={(e) => handleInputChange('include_quiz', e.target.checked)}
                  />
                  <div>
                    <strong>Interactive Quiz</strong>
                    <p>Engage visitors with an assessment that captures leads</p>
                  </div>
                </label>

                {formData.include_quiz && (
                  <div className="form-group" style={{ marginLeft: '2rem', marginTop: '1rem' }}>
                    <GlassSelect
                      label="Select Quiz"
                      value={formData.existing_quiz_id || ''}
                      onChange={(value) => handleInputChange('existing_quiz_id', value)}
                      options={[
                        { value: '', label: '✨ Generate New Quiz' },
                        ...existingQuizzes.map(quiz => ({
                          value: quiz.quiz_id,
                          label: `${quiz.name} (${quiz.questions?.length || 0} questions)`
                        }))
                      ]}
                    />

                    {!formData.existing_quiz_id && (
                      <>
                        <label>Quiz Purpose</label>
                        <textarea
                          className="glass-textarea"
                          value={formData.quiz_purpose || ''}
                          onChange={(e) => handleInputChange('quiz_purpose', e.target.value)}
                          placeholder="What should this quiz assess or qualify?"
                          rows={3}
                          style={{
                            width: '100%',
                            padding: 'var(--space-3) var(--space-4)',
                            fontSize: 'var(--text-base)',
                            fontFamily: 'var(--font-family)',
                            color: 'var(--text)',
                            background: 'var(--panel)',
                            border: '1px solid var(--stroke)',
                            borderRadius: 'var(--radius-md)',
                            backdropFilter: 'blur(20px)',
                            resize: 'vertical',
                            outline: 'none'
                          }}
                        />
                        <AIFieldGenerator
                          fieldType="quiz_purpose"
                          projectId={formData.project_id}
                          currentValue={formData.quiz_purpose}
                          context={{
                            stream: formData.stream,
                            campaign_goal: formData.campaign_goal,
                            title: formData.title
                          }}
                          onAccept={(content) => handleInputChange('quiz_purpose', content)}
                          label="Quiz Purpose"
                          placeholder="Generate quiz purpose and assessment criteria based on your campaign"
                        />
                        <small>Describe what the quiz should evaluate or what leads should be qualified for</small>
                      </>
                    )}
                  </div>
                )}

                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.include_lead_form}
                    onChange={(e) => handleInputChange('include_lead_form', e.target.checked)}
                  />
                  <div>
                    <strong>Lead Capture Form</strong>
                    <p>Contact form to collect visitor information</p>
                  </div>
                </label>

                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.include_testimonials}
                    onChange={(e) => handleInputChange('include_testimonials', e.target.checked)}
                  />
                  <div>
                    <strong>Testimonials Section</strong>
                    <p>Build trust with client success stories</p>
                  </div>
                </label>

                {formData.include_testimonials && (
                  <div className="form-group" style={{ marginLeft: '2rem', marginTop: '1rem' }}>
                    <label>Testimonials Content</label>
                    <textarea
                      className="glass-textarea"
                      value={formData.testimonials_content || ''}
                      onChange={(e) => handleInputChange('testimonials_content', e.target.value)}
                      placeholder="Paste real testimonials or generate examples"
                      rows={4}
                      style={{
                        width: '100%',
                        padding: 'var(--space-3) var(--space-4)',
                        fontSize: 'var(--text-base)',
                        fontFamily: 'var(--font-family)',
                        color: 'var(--text)',
                        background: 'var(--panel)',
                        border: '1px solid var(--stroke)',
                        borderRadius: 'var(--radius-md)',
                        backdropFilter: 'blur(20px)',
                        resize: 'vertical',
                        outline: 'none'
                      }}
                    />
                    <AIFieldGenerator
                      fieldType="testimonials"
                      projectId={formData.project_id}
                      currentValue={formData.testimonials_content}
                      context={{
                        stream: formData.stream,
                        campaign_goal: formData.campaign_goal
                      }}
                      onAccept={(content) => handleInputChange('testimonials_content', content)}
                      label="Testimonials"
                      placeholder="Generate example testimonials (replace with real ones before publishing)"
                    />
                    <small style={{ color: 'var(--danger)' }}>⚠️ Generated testimonials are examples - replace with real testimonials before publishing</small>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--divider)' }}>
                <label>Our Services / Practice Areas</label>
                <textarea
                  className="glass-textarea"
                  value={formData.services_content || ''}
                  onChange={(e) => handleInputChange('services_content', e.target.value)}
                  placeholder="Define specific services (e.g. Will Drafting: Protect your legacy with...)"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: 'var(--space-3) var(--space-4)',
                    fontSize: 'var(--text-base)',
                    fontFamily: 'var(--font-family)',
                    color: 'var(--text)',
                    background: 'var(--panel)',
                    border: '1px solid var(--stroke)',
                    borderRadius: 'var(--radius-md)',
                    backdropFilter: 'blur(20px)',
                    resize: 'vertical',
                    outline: 'none'
                  }}
                />
                <AIFieldGenerator
                  fieldType="services"
                  projectId={formData.project_id}
                  currentValue={formData.services_content}
                  context={{
                    campaign_goal: formData.campaign_goal,
                    value_proposition: formData.value_proposition
                  }}
                  onAccept={(content) => handleInputChange('services_content', content)}
                  label="Our Services"
                  placeholder="Identify services from project description and industry"
                />
                <small>Define 3-4 primary offerings to display on the landing page</small>
              </div>

              <div className="checkbox-group">
                <label className="checkbox-option">
                  <input
                    type="checkbox"
                    checked={formData.include_faq}
                    onChange={(e) => handleInputChange('include_faq', e.target.checked)}
                  />
                  <div>
                    <strong>FAQ Section</strong>
                    <p>Answer common questions</p>
                  </div>
                </label>

                {formData.include_faq && (
                  <div className="form-group" style={{ marginLeft: '2rem', marginTop: '1rem' }}>
                    <label>Questions to Address</label>
                    <textarea
                      className="glass-textarea"
                      value={formData.faq_questions || ''}
                      onChange={(e) => handleInputChange('faq_questions', e.target.value)}
                      placeholder="List questions, one per line"
                      rows={5}
                      style={{
                        width: '100%',
                        padding: 'var(--space-3) var(--space-4)',
                        fontSize: 'var(--text-base)',
                        fontFamily: 'var(--font-family)',
                        color: 'var(--text)',
                        background: 'var(--panel)',
                        border: '1px solid var(--stroke)',
                        borderRadius: 'var(--radius-md)',
                        backdropFilter: 'blur(20px)',
                        resize: 'vertical',
                        outline: 'none'
                      }}
                    />
                    <AIFieldGenerator
                      fieldType="faq_questions"
                      projectId={formData.project_id}
                      currentValue={formData.faq_questions}
                      context={{
                        stream: formData.stream,
                        campaign_goal: formData.campaign_goal,
                        value_proposition: formData.value_proposition
                      }}
                      onAccept={(content) => handleInputChange('faq_questions', content)}
                      label="FAQ Questions"
                      placeholder="Generate common questions your target audience might ask"
                    />
                    <small>List the key questions prospects ask about this service/offer</small>
                  </div>
                )}
              </div>

              {error && (
                <div className="error-message" style={{ color: 'var(--danger)', marginTop: 'var(--space-4)' }}>
                  {error}
                </div>
              )}
            </div>
          </GlassCard>
        );

      default:
        return null;
    }
  };

  return (
    <div className="landing-page-creator">
      <ModuleHeader
        title="Create Landing Page"
        breadcrumb={['Home', 'Landing Pages', 'Create']}
        actions={[
          <GlassButton key="cancel" variant="ghost" onClick={() => onCancel ? onCancel() : navigate('/landing-pages')}>
            Cancel
          </GlassButton>
        ]}
      />

      <GlassCard variant="default" padding="md" className="step-indicator-card">
        <div className="step-indicator">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className={`step ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}>
              <div className="step-number">{s}</div>
              <div className="step-label">
                {s === 1 && 'Template'}
                {s === 2 && 'Details'}
                {s === 3 && 'Design'}
                {s === 4 && 'Features'}
              </div>
            </div>
          ))}
        </div>
      </GlassCard>

      <div className="creator-content">
        {renderStep()}
      </div>

      <GlassCard variant="strong" padding="md" className="creator-footer-card">
        <div className="creator-footer">
          {step > 1 && (
            <GlassButton variant="ghost" onClick={() => setStep(step - 1)}>
              Previous
            </GlassButton>
          )}

          <div className="spacer" />

          {step < 4 ? (
            <GlassButton
              variant="primary"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
            >
              Next
            </GlassButton>
          ) : (
            <GlassButton
              variant="primary"
              onClick={handleGenerate}
              disabled={!canProceed() || isGenerating}
              loading={isGenerating}
            >
              {isGenerating ? 'Generating with AI...' : 'Generate Landing Page'}
            </GlassButton>
          )}
        </div>
      </GlassCard>
    </div>
  );
};

export default LandingPageCreator;
